<!--
  订单跟进记录（原型：订单 - 订单跟进 - 订单跟进记录）

  两个页签共用一个 /crm/order-follow-up/page 接口：
  - 「全部跟进记录」→ onlyMine = false（不过滤）
  - 「我的跟进记录」→ onlyMine = true（后端强制 followerUserId = 当前登录用户）

  ★ 页签切换必须立刻把 onlyMine 写进 queryParams（初值也要写），
    不能留 undefined —— qs.stringify 会把 undefined 参数直接丢掉。
-->
<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="90px"
    >
      <el-form-item label="关键字" prop="keyword">
        <el-input
          v-model="queryParams.keyword"
          class="!w-260px"
          clearable
          placeholder="单号/项目名称/跟进人"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="人员" prop="followerUserId">
        <el-select
          v-model="queryParams.followerUserId"
          class="!w-180px"
          clearable
          filterable
          placeholder="请选择"
        >
          <el-option
            v-for="item in userOptions"
            :key="item.id"
            :label="item.nickname"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="跟进方式" prop="type">
        <el-select v-model="queryParams.type" class="!w-160px" clearable placeholder="请选择">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_FOLLOW_UP_TYPE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="项目状态" prop="orderStatus">
        <el-select
          v-model="queryParams.orderStatus"
          class="!w-160px"
          clearable
          placeholder="全部状态"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_ORDER_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="跟进时间" prop="followTimes">
        <el-date-picker
          v-model="queryParams.followTimes"
          :default-time="defaultTime"
          class="!w-360px"
          end-placeholder="结束日期"
          range-separator="-"
          start-placeholder="开始日期"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['crm:order-follow-up:create']"
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:order-follow-up:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <!-- 口径页签 -->
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <el-tab-pane v-for="def in TAB_DEFS" :key="def.name" :label="def.label" :name="def.name" />
    </el-tabs>

    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column
        align="center"
        fixed="left"
        label="项目名称"
        prop="projectName"
        width="200"
      />
      <el-table-column align="center" label="项目经理" prop="projectManagerName" width="120" />
      <el-table-column align="center" label="跟进方式" prop="type" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_FOLLOW_UP_TYPE" :value="scope.row.type" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="跟进内容" prop="content" width="260" />
      <el-table-column align="center" label="开发汇总" prop="devSummary" width="220" />
      <el-table-column align="center" label="Bug汇总" prop="bugSummary" width="220" />
      <el-table-column align="center" label="今日工作内容" prop="todayWork" width="220" />
      <el-table-column align="center" label="下一步工作计划" prop="nextPlan" width="220" />
      <el-table-column align="center" label="待处理事项" prop="pendingMatter" width="180" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="跟进时间"
        prop="followTime"
        width="180"
      />
      <el-table-column align="center" label="申请人" prop="followerUserName" width="110" />
      <el-table-column align="center" label="申请人部门" prop="followerDeptName" width="150" />
      <el-table-column align="center" label="单号" prop="no" width="130" />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="120"
      >
        <template #default="scope">
          <div class="crm-order-followup-action">
            <el-button
              v-hasPermi="['crm:order-follow-up:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:order-follow-up:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <OrderFollowUpForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import download from '@/utils/download'
import { OrderFollowUpApi, OrderFollowUpVO } from '@/api/crm/orderFollowUp'
import * as UserApi from '@/api/system/user'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import OrderFollowUpForm from './OrderFollowUpForm.vue'

defineOptions({ name: 'CrmOrderFollowUpRecord' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

/** 两个页签的口径定义；onlyMine 会写进 queryParams 一起提交 */
const TAB_DEFS = [
  { name: 'all', label: '全部跟进记录', onlyMine: false },
  { name: 'mine', label: '我的跟进记录', onlyMine: true }
]

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref<OrderFollowUpVO[]>([]) // 列表的数据
const userOptions = ref<UserApi.UserVO[]>([]) // 人员下拉
const exportLoading = ref(false) // 导出的加载中
const activeTab = ref('all') // 当前页签
/** 日期范围默认带时分秒 00:00:00 ~ 23:59:59 */
const defaultTime = [new Date(2000, 0, 1, 0, 0, 0), new Date(2000, 0, 1, 23, 59, 59)]

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 10,
  onlyMine: false, // 初值必须与 activeTab 一致，且不能是 undefined
  keyword: '',
  followerUserId: undefined,
  type: undefined,
  orderStatus: undefined,
  followTimes: []
})
const queryFormRef = ref() // 搜索的表单

/** 把页签口径写入查询条件 */
const applyTabFilter = (name: string) => {
  const def = TAB_DEFS.find((item) => item.name === name)
  queryParams.onlyMine = def ? def.onlyMine : false
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await OrderFollowUpApi.getOrderFollowUpPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 切换页签：先换口径再查询 */
const handleTabChange = (name: string | number) => {
  applyTabFilter(String(name))
  handleQuery()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.keyword = ''
  queryParams.followerUserId = undefined
  queryParams.type = undefined
  queryParams.orderStatus = undefined
  queryParams.followTimes = []
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref<InstanceType<typeof OrderFollowUpForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await OrderFollowUpApi.deleteOrderFollowUp(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await OrderFollowUpApi.exportOrderFollowUp(queryParams)
    download.excel(data, '订单跟进记录.xlsx')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 初始化 **/
onMounted(async () => {
  applyTabFilter(activeTab.value)
  userOptions.value = await UserApi.getSimpleUserList()
  await getList()
})
</script>

<style lang="scss" scoped>
.crm-order-followup-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
